import React, { Fragment, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button }from '@cmsgov/design-system';


import { APP_STATUSES } from '@constants/individualPortal/dashboard/dashboardModelTypes';
import { ctaHandlers } from '@utils/dashboard/stateSpecificUtils';
import { configs } from '@constants/configs/configs';
import { camelCase } from '@utils/miscellaneous/objectHelpers';
import { history } from '@store/appStore';

const AppActions = (props) => {
    const {
        envData: {
            stateCode,
            isState,
            isStateFullStack,
            isMNFullStack,
        },
        latestApp,
        data: {
            applicationStatus,
            applicationYear,
            caseNumber,
            encryptedCaseNumber,
            onCancelApp,
            applicationDentalStatus,
            isLocked,
        }
    } = props;

    const { goToViewAppSummary, goToResumeApp, goToPlanSummary, goToUploadDocs, goToEligibilityResults } = ctaHandlers;

    const ssapParams = { caseNumber, applicationStatus, applicationYear,  fromMyApp: true };

    const CTA_LIST = {
        RESUME_APP: {
            id: 'application_status.action.resume_application',
            cb: () => { goToResumeApp(stateCode, ssapParams, isStateFullStack); },
            icon: 'arrow-right',
            visibility: isStateFullStack
        },
        BACK_TO_DASHBOARD: {
            id: 'button.go_to_dashboard',
            cb: () => { history.push(configs.routes.INDPORTAL); },
            icon: 'th-large',
            visibility: true
        },
        VIEW_ELIGIBILITY: {
            id: 'button.view_eligibility',
            cb: () => {  goToEligibilityResults(stateCode, encryptedCaseNumber); },
            icon: 'clipboard-check',
            visibility: true
        },
        VERIFICATION_DOCS: {
            id: 'button.verification_documents',
            cb: () => { goToUploadDocs(caseNumber, stateCode, isMNFullStack); },
            icon: 'file-upload',
            visibility: isStateFullStack
        },
        VIEW_PLAN_DETAILS: {
            id: 'button.view_plan_details',
            cb: () => { goToPlanSummary(caseNumber); },
            icon: 'first-aid',
            visibility: true
        },
        VIEW_PRINT_SUMMARY: {
            id: 'button.view_and_print_app_summary',
            cb: () => { goToViewAppSummary(stateCode, ssapParams); },
            icon: 'print',
            visibility: true
        },
        CANCEL_APP: {
            id: 'application_status.action.cancel_application',
            cb: () => { onCancelApp(caseNumber); },
            icon: 'times',
            visibility: isStateFullStack && !isLocked
        },
    };

    const ACTION_TYPES = {
        PRIMARY: 'primary',
        SECONDARY: 'secondary'
    };

    const getCTAGroupHeader = (actionType, appStatus) => {
        let msgId = '';
        if (actionType === ACTION_TYPES.PRIMARY) {
            if ([APP_STATUSES.ER, APP_STATUSES.PN, APP_STATUSES.EN].includes(appStatus)) {
                msgId = 'applications.next_steps';
            } else {
                msgId = 'applications.in_progress';
            }
        } else {
            msgId = 'applications.actions';
        }
        return (
            <FormattedMessage
                id={msgId}
                values={{ strong: chunks => <strong>{chunks}</strong> }}
            />
        );
    };

    const getCTAs = (appStatus) => {
        const appCTAs = {
            primary: [],
            secondary: []
        };

        switch (appStatus) {
            case APP_STATUSES.OP:
            case APP_STATUSES.UC:
                appCTAs.primary.push(CTA_LIST.RESUME_APP);
                appCTAs.secondary.push(CTA_LIST.CANCEL_APP);
                break;
            case APP_STATUSES.SG:
            case APP_STATUSES.SU:
                appCTAs.secondary.push(CTA_LIST.CANCEL_APP);
                break;
            case APP_STATUSES.ER:
                appCTAs.primary.push(CTA_LIST.BACK_TO_DASHBOARD);
                if (![APP_STATUSES.PN, APP_STATUSES.EN].includes(applicationDentalStatus)) appCTAs.secondary.push(CTA_LIST.CANCEL_APP);
                appCTAs.secondary.push(CTA_LIST.VIEW_ELIGIBILITY);
                latestApp && appCTAs.secondary.push(CTA_LIST.VERIFICATION_DOCS);
                appCTAs.secondary.push(CTA_LIST.VIEW_PRINT_SUMMARY);
                break;
            case APP_STATUSES.EN:
            case APP_STATUSES.PN:
                appCTAs.primary.push(CTA_LIST.BACK_TO_DASHBOARD);
                appCTAs.secondary.push(CTA_LIST.VIEW_ELIGIBILITY);
                latestApp && appCTAs.secondary.push(CTA_LIST.VERIFICATION_DOCS);
                appCTAs.secondary.push(CTA_LIST.VIEW_PLAN_DETAILS);
                appCTAs.secondary.push(CTA_LIST.VIEW_PRINT_SUMMARY);
                break;

            case APP_STATUSES.CL:
                appCTAs.secondary.push(CTA_LIST.VIEW_ELIGIBILITY);
                appCTAs.secondary.push(CTA_LIST.VIEW_PRINT_SUMMARY);
                break;
            default:
                return appCTAs;
        }

        return appCTAs;
    };

    const appCTAs = useMemo(() => getCTAs(applicationStatus), [applicationStatus]);
    return (
        <div className='application__actions'>
            {
                Object.keys(appCTAs).map(key => (
                    <Fragment key={key}>
                        {
                            appCTAs[key].length > 0 && appCTAs[key].some(({ visibility }) => visibility === true) &&
                            <p>{getCTAGroupHeader(key, applicationStatus)}</p>
                        }
                        <div className='grid-row'>
                            {
                                appCTAs[key].map(({ id, cb, icon, visibility }, index) => visibility && (
                                    <div
                                        key={id}
                                        className={`desktop:grid-col-${appCTAs[key].length - 1 === index ? 6 : 4} tablet:grid-col-6 mobile:grid-col-12 padding-1`}
                                    >
                                        <Button
                                            id={`applications${camelCase(id)}`}
                                            className={`usa-button gi-button gi-button__${key}`}
                                            onClick={cb}
                                        >
                                            {icon && <FontAwesomeIcon icon={icon} />}
                                            <FormattedMessage id={id} />
                                        </Button>
                                    </div>
                                ))
                            }
                        </div>
                    </Fragment>
                ))
            }
        </div>
    );
};

export default AppActions;